import type { RouteRecord } from "vite-react-ssg";
import { Navigate, useLocation } from "react-router-dom";
import Layout from "./Layout";
import Index from "./pages/Index";

// Preserves query string (UTM params etc.) when redirecting legacy paths
const RedirectWithQuery = ({ to }: { to: string }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

const lazyDefault = (loader: () => Promise<{ default: React.ComponentType<unknown> }>) =>
  async () => {
    const mod = await loader();
    return { Component: mod.default };
  };

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: Layout,
    children: [
      { index: true, Component: Index, entry: "src/pages/Index.tsx" },

      // Versicherungen
      { path: "versicherungen", lazy: lazyDefault(() => import("./pages/VersicherungenUeberblickPage")) },
      { path: "versicherungen/krankenversicherung", lazy: lazyDefault(() => import("./pages/KrankenversicherungPage")) },
      { path: "versicherungen/berufsunfaehigkeit", lazy: lazyDefault(() => import("./pages/BerufsunfaehigkeitPage")) },
      { path: "versicherungen/krankentagegeld", lazy: lazyDefault(() => import("./pages/KrankentagegeldPage")) },
      { path: "versicherungen/haftpflicht", lazy: lazyDefault(() => import("./pages/HaftpflichtPage")) },
      { path: "versicherungen/sonstige", lazy: lazyDefault(() => import("./pages/SonstigeVersicherungenPage")) },
      { path: "versicherungen/rechtsschutz", lazy: lazyDefault(() => import("./pages/RechtsschutzPage")) },
      { path: "versicherungen/hausrat", lazy: lazyDefault(() => import("./pages/HausratPage")) },
      { path: "versicherungen/pflege", lazy: lazyDefault(() => import("./pages/PflegeversicherungPage")) },
      { path: "versicherungen/auslandskranken", lazy: lazyDefault(() => import("./pages/AuslandskrankenPage")) },

      // Altersvorsorge
      { path: "altersvorsorge", lazy: lazyDefault(() => import("./pages/AltersvorsorgeUeberblickPage")) },
      { path: "altersvorsorge/ueberblick", lazy: lazyDefault(() => import("./pages/AltersvorsorgeUeberblickPage")) },
      { path: "altersvorsorge/versorgungswerk", lazy: lazyDefault(() => import("./pages/VersorgungswerkPage")) },
      { path: "altersvorsorge/private-vorsorge", lazy: lazyDefault(() => import("./pages/PrivateVorsorgePage")) },
      { path: "altersvorsorge/betriebliche", lazy: lazyDefault(() => import("./pages/BetrieblicheAltersvorsorgePage")) },
      { path: "altersvorsorge/riester", lazy: lazyDefault(() => import("./pages/RiesterPage")) },
      { path: "altersvorsorge/basis", lazy: lazyDefault(() => import("./pages/BasisrentePage")) },
      { path: "altersvorsorge/altersvorsorgedepot", lazy: lazyDefault(() => import("./pages/AltersvorsorgedepotPage")) },
      { path: "altersvorsorge/vergleichsrechner", lazy: lazyDefault(() => import("./pages/VorsorgeVergleichsrechnerPage")) },

      // Investment
      { path: "investment", lazy: lazyDefault(() => import("./pages/InvestmentUeberblickPage")) },
      { path: "investment/ueberblick", lazy: lazyDefault(() => import("./pages/InvestmentUeberblickPage")) },
      { path: "investment/konten", lazy: lazyDefault(() => import("./pages/KontenPage")) },
      { path: "investment/etfs-fonds-aktien", lazy: lazyDefault(() => import("./pages/EtfsFondsAktienPage")) },
      { path: "investment/immobilien", lazy: lazyDefault(() => import("./pages/ImmobilienPage")) },
      { path: "investment/vl", lazy: lazyDefault(() => import("./pages/VermoegenswirksameLeistungenPage")) },

      // Beiträge
      { path: "beitraege", lazy: lazyDefault(() => import("./pages/BeitraegePage")) },
      { path: "beitraege/:slug", lazy: lazyDefault(() => import("./pages/BeitragPage")) },

      // Über uns
      { path: "ueber-uns", lazy: lazyDefault(() => import("./pages/UeberUnsPage")) },


      // Beratung
      { path: "beratung", lazy: lazyDefault(() => import("./pages/PlaceholderPage")) },
      { path: "beratung/individuell", lazy: lazyDefault(() => import("./pages/IndividuelleBeratungPage")) },
      { path: "beratung/seminare", lazy: lazyDefault(() => import("./pages/OnlineSeminarePage")) },

      // Legal
      { path: "impressum", lazy: lazyDefault(() => import("./pages/ImpressumPage")) },
      { path: "datenschutz", lazy: lazyDefault(() => import("./pages/DatenschutzPage")) },

      // Tools
      { path: "tools/testimonials-export", lazy: lazyDefault(() => import("./pages/TestimonialsExportPage")) },
      { path: "tools/altersvorsorgedepot", Component: () => <RedirectWithQuery to="/altersvorsorge/altersvorsorgedepot" /> },
      { path: "tools/brutto-netto-rechner", lazy: lazyDefault(() => import("./pages/BruttoNettoRechnerPage")) },
      { path: "tools/versorgungsluecken-rechner", lazy: lazyDefault(() => import("./pages/VersorgungsluckenRechnerPage")) },
      { path: "tools/rentenluecken-rechner", lazy: lazyDefault(() => import("./pages/RentenlueckenRechnerPage")) },

      { path: "zinseszinsrechner", lazy: lazyDefault(() => import("./pages/ZinseszinsrechnerPage")) },
      { path: "inflationsrechner", lazy: lazyDefault(() => import("./pages/InflationsrechnerPage")) },

      // Standalone (nicht verlinkt, noindex)
      { path: "krankenversicherung-sparpotenzial", lazy: lazyDefault(() => import("./pages/GkvSparpotenzialPage")) },
      { path: "basisrente-foerderrechner", lazy: lazyDefault(() => import("./pages/BasisrenteFoerderrechnerPage")) },

      // Legacy redirects
      { path: "krankenversicherungsrechner", lazy: lazyDefault(() => import("./pages/KrankenversicherungsrechnerRedirect")) },
      { path: "individuelle-beratung", Component: () => <RedirectWithQuery to="/beratung/individuell" /> },

      // Meta-Ads Landing Pages
      { path: "unser-geschenk-an-dich", lazy: lazyDefault(() => import("./pages/LandingPageInfos")) },
      { path: "schluss-mit-dem-halbwissen", lazy: lazyDefault(() => import("./pages/LandingPageVideo")) },

      // Legacy redirects
      { path: "lp/info", Component: () => <RedirectWithQuery to="/unser-geschenk-an-dich" /> },
      { path: "lp/infos", Component: () => <RedirectWithQuery to="/unser-geschenk-an-dich" /> },
      { path: "lp/video", Component: () => <RedirectWithQuery to="/schluss-mit-dem-halbwissen" /> },
      { path: "betriebliche-altersvorsorge-1", Component: () => <RedirectWithQuery to="/altersvorsorge/betriebliche" /> },
      { path: "private-vorsorge-1", Component: () => <RedirectWithQuery to="/altersvorsorge/private-vorsorge" /> },
      { path: "basis-rente", Component: () => <RedirectWithQuery to="/altersvorsorge/basis" /> },
      { path: "datenschutzerklaerung", Component: () => <RedirectWithQuery to="/datenschutz" /> },

      // Catch-all
      { path: "*", lazy: lazyDefault(() => import("./pages/NotFound")) },
    ],
  },
];

export default routes;
